/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { TFunction } from 'i18next';
import {
  AIRASIA_CODES,
  ALL,
  CABIN_CLASS,
  POPULAR_FILTER,
  RECOMMENDED_SORT,
  STOP_TYPE,
  CabinClassType,
} from '../config/constants';
import type { SearchFlightFilters } from '../hooks/flights';

export type SortFilterTranslation = Record<string, string>;

export type ChipFilterOption = {
  text: string;
  value: string;
  isSelected: boolean;
  disabled: boolean;
  showIcon: boolean;
};

export type SortFilterData = ChipFilterOption;
export type StopsFilterData = ChipFilterOption;
export type CabinClassFilterData = ChipFilterOption & {
  value: CabinClassType;
};

export type PopularFiltersData = {
  label: string;
  value: (typeof POPULAR_FILTER)[keyof typeof POPULAR_FILTER];
};

export const buildSortFilterData = (
  sortOptions?: unknown,
  translation?: unknown,
  t?: TFunction,
): SortFilterData[] => {
  if (!Array.isArray(sortOptions)) return [];
  const sortTranslation =
    translation && typeof translation === 'object'
      ? (translation as SortFilterTranslation)
      : undefined;

  return sortOptions
    .filter((option): option is string => typeof option === 'string')
    .map((option) => ({
      text:
        sortTranslation?.[option] ||
        t?.(`flightsListingPage.sort.${option}`, { defaultValue: option }) ||
        option,
      value: option,
      isSelected: false,
      disabled: false,
      showIcon: false,
    }));
};

/**
 * Decides which sort to apply when the user presses a sort chip, and whether
 * the "Recommended" snackbar should be shown (on any transition back to
 * Recommended). An undefined requested sort means the
 * active chip was deselected, which falls back to the default sort.
 */
export const resolveSortTransition = ({
  previousSort,
  requestedSort,
  defaultSort,
}: {
  previousSort?: string;
  requestedSort?: string;
  defaultSort?: string;
}): { nextSort?: string; showRecommendedSnackbar: boolean } => {
  const prev = previousSort ?? defaultSort;
  const nextSort = requestedSort ?? defaultSort;

  return {
    nextSort,
    showRecommendedSnackbar:
      prev !== RECOMMENDED_SORT && nextSort === RECOMMENDED_SORT,
  };
};

export const buildStopsData = (
  translation?: unknown,
  t?: TFunction,
): StopsFilterData[] => {
  const stopsTranslation =
    translation && typeof translation === 'object'
      ? (translation as Record<string, string>)
      : undefined;

  const stopsConfig = [
    {
      translationKey: 'any',
      i18nKey: 'flightsListingPage.stops_filter.any',
      value: STOP_TYPE.ANY,
    },
    {
      translationKey: 'nonStop',
      i18nKey: 'flightsListingPage.stops_filter.non_stop',
      value: STOP_TYPE.NON_STOP,
    },
    {
      translationKey: 'upto_one',
      i18nKey: 'flightsListingPage.stops_filter.one_stop',
      value: STOP_TYPE.UPTO_1,
    },
    {
      translationKey: 'upto_two',
      i18nKey: 'flightsListingPage.stops_filter.two_stop',
      value: STOP_TYPE.UPTO_2,
    },
  ] as const;

  return stopsConfig.map(({ translationKey, i18nKey, value }) => ({
    text: stopsTranslation?.[translationKey] || t?.(i18nKey) || translationKey,
    value,
    isSelected: false,
    disabled: false,
    showIcon: false,
  }));
};

export const buildCabinClassData = (
  translation?: unknown,
  t?: TFunction,
): CabinClassFilterData[] => {
  const cabinClassTranslation =
    translation && typeof translation === 'object'
      ? (translation as Record<string, string>)
      : undefined;

  const cabinClassConfig = [
    {
      translationKey: 'economy',
      i18nKey: 'flightsListingPage.cabin_filter.economy',
      value: CABIN_CLASS.ECONOMY,
    },
    {
      translationKey: 'premium_economy',
      i18nKey: 'flightsListingPage.cabin_filter.premium_economy',
      value: CABIN_CLASS.PREMIUM_ECONOMY,
    },
    {
      translationKey: 'business',
      i18nKey: 'flightsListingPage.cabin_filter.business',
      value: CABIN_CLASS.BUSINESS,
    },
    {
      translationKey: 'first',
      i18nKey: 'flightsListingPage.cabin_filter.first_class',
      value: CABIN_CLASS.FIRST_CLASS,
    },
  ] as const;

  return cabinClassConfig.map(({ translationKey, i18nKey, value }) => ({
    text:
      cabinClassTranslation?.[translationKey] || t?.(i18nKey) || translationKey,
    value,
    isSelected: false,
    disabled: false,
    showIcon: false,
  }));
};

export const buildPopularFiltersData = (
  translation?: unknown,
  t?: TFunction,
): PopularFiltersData[] => {
  const popularFilterTranslation =
    translation && typeof translation === 'object'
      ? (translation as Record<string, string>)
      : undefined;

  const popularFilterConfig = [
    {
      translationKey: 'aaFlightsOnly',
      i18nKey: 'flightsSearchPage.main.airAsiaFlightsOnlyLabel',
      value: POPULAR_FILTER.AIR_ASIA_FLIGHTS_ONLY,
    },
    {
      translationKey: 'directFlights',
      i18nKey: 'flightsSearchPage.main.directFlightsLabel',
      value: POPULAR_FILTER.DIRECT_FLIGHTS,
    },
    {
      translationKey: 'baggageIncluded',
      i18nKey: 'flightsListingPage.checkedBaggageIncluded',
      value: POPULAR_FILTER.CHECKED_BAGGAGE_INCLUDED,
    },
  ] as const;

  return popularFilterConfig.map(({ translationKey, i18nKey, value }) => ({
    label:
      popularFilterTranslation?.[translationKey] ||
      t?.(i18nKey) ||
      translationKey,
    value,
  }));
};

// -------------------- Carrier selection rules --------------------

export type CarrierSelectionState = Pick<
  SearchFlightFilters,
  'allowAllCarriers' | 'onlyAllowedCarriers' | 'isAllCarriersSelected'
>;

export type CarrierSelectionAction =
  | { type: 'toggleAll' }
  | { type: 'toggleCarrier'; code: string };

const isAllCode = (code: string) => code.toLowerCase() === ALL.toLowerCase();

/**
 * Airlines has three UI states, all driven by CarrierSelectionState:
 * - blank (default): allowAllCarriers=true, isAllCarriersSelected=false -> nothing ticked
 * - all ticked:      allowAllCarriers=true, isAllCarriersSelected=true  -> every row ticked
 * - subset:          allowAllCarriers=false                             -> only onlyAllowedCarriers ticked
 * Blank and all ticked produce the same API payload (all carriers).
 */
export const isAllCarriersTicked = (state: CarrierSelectionState) =>
  state.allowAllCarriers && Boolean(state.isAllCarriersSelected);

// Carrier codes that should render as ticked (never includes ALL)
export const getTickedCarrierCodes = (
  state: CarrierSelectionState,
  allCarrierCodes: readonly string[],
): string[] => {
  if (state.allowAllCarriers) {
    return state.isAllCarriersSelected ? [...allCarrierCodes] : [];
  }
  return state.onlyAllowedCarriers ?? [];
};

export const resolveCarrierSelection = (
  state: CarrierSelectionState,
  action: CarrierSelectionAction,
  allCarrierCodes: readonly string[],
): Required<CarrierSelectionState> => {
  const ticked = getTickedCarrierCodes(state, allCarrierCodes);

  let next: string[];
  if (action.type === 'toggleAll') {
    next = isAllCarriersTicked(state) ? [] : [...allCarrierCodes];
  } else {
    next = ticked.includes(action.code)
      ? ticked.filter((code) => code !== action.code)
      : [...ticked, action.code];
  }

  const sanitized = next.filter((code) => !isAllCode(code));

  // Nothing ticked -> back to blank (search all carriers)
  if (sanitized.length === 0) {
    return {
      allowAllCarriers: true,
      onlyAllowedCarriers: [...allCarrierCodes],
      isAllCarriersSelected: false,
    };
  }

  // Every available carrier ticked -> "All airlines" ticked
  const isEveryCarrierTicked =
    allCarrierCodes.length > 0 &&
    allCarrierCodes.every((code) => sanitized.includes(code));

  if (isEveryCarrierTicked) {
    return {
      allowAllCarriers: true,
      onlyAllowedCarriers: [...allCarrierCodes],
      isAllCarriersSelected: true,
    };
  }

  return {
    allowAllCarriers: false,
    onlyAllowedCarriers: sanitized,
    isAllCarriersSelected: false,
  };
};

// -------------------- Direct flights <-> Stops rule --------------------

type DirectFlightsPatch = Partial<
  Pick<SearchFlightFilters, 'isDirectFlights' | 'stopType'>
>;

/**
 * Keeps the "Direct flights" popular filter and the Stops chips in agreement.
 * - stopType patched: Direct chip <=> isDirectFlights=true, anything else => false
 * - isDirectFlights patched: true => Direct chip, false => Any
 * stopType wins when both are provided.
 */
export const syncDirectFlightsWithStops = <T extends DirectFlightsPatch>(
  patch: T,
): T => {
  if (patch.stopType !== undefined) {
    return {
      ...patch,
      isDirectFlights: patch.stopType === STOP_TYPE.NON_STOP,
    };
  }

  if (patch.isDirectFlights !== undefined) {
    return {
      ...patch,
      stopType: patch.isDirectFlights ? STOP_TYPE.NON_STOP : STOP_TYPE.ANY,
    };
  }

  return patch;
};

// -------------------- Listing filter reset --------------------

export type ListingFilterResetOptions = {
  // Cart deep link forces AirAsia-only carriers
  hasCartDeepLink?: boolean;
  // Search widget keeps the user's AirAsia-only checkbox from the search form
  includeAirAsiaOnly?: boolean;
};

// Single source of truth for "reset listing filters" (sort, popular, stops, durations, airlines).
export const buildListingFilterResetPatch = (
  defaults: SearchFlightFilters,
  {
    hasCartDeepLink = false,
    includeAirAsiaOnly = true,
  }: ListingFilterResetOptions = {},
): Partial<SearchFlightFilters> => ({
  sort: undefined,
  ...(includeAirAsiaOnly
    ? {
        isAirAsiaFlightsOnly: hasCartDeepLink
          ? true
          : defaults.isAirAsiaFlightsOnly,
      }
    : {}),
  isCheckBagsIncluded: defaults.isCheckBagsIncluded,
  isDirectFlights: defaults.isDirectFlights,
  stopType: defaults.stopType,
  allowOvernight: defaults.allowOvernight,
  maxTravelTimeInHrs: defaults.maxTravelTimeInHrs,
  minStopoverTimeInHrs: defaults.minStopoverTimeInHrs,
  maxStopoverTimeInHrs: defaults.maxStopoverTimeInHrs,
  allowAllCarriers: hasCartDeepLink ? false : defaults.allowAllCarriers,
  onlyAllowedCarriers: hasCartDeepLink
    ? [...AIRASIA_CODES]
    : defaults.onlyAllowedCarriers,
  excludedCarriers: defaults.excludedCarriers,
  isAllCarriersSelected: false,
});
